import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {AutoComplete} from 'primereact/autocomplete'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {InputText} from 'primereact/inputtext'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react'
import {useParams} from 'react-router-dom'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {treatAsRole} from '../patient/PatientConstants'
import {useAuth} from './AuthProvider'
import {
    activateDevice,
    deactivateDevice,
    getActiveDeviceByUser,
    getListApplicationCodes,
    getListDevices,
} from './WatchRemoteEndpoint'

const QueryKey = 'DeviceOptions'
const ApplicationCodeQueryKey = 'Application-Code-Options'

const WatchRemoteActivation = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    const queryClient = useQueryClient()
    const {user} = useAuth()
    const {translateKeys, translateCommons} = TranslateUtils()

    const {id: idString} = useParams()
    const id = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])

    const [myDevice, setMyDevice] = useState(null)
    const [hasActiveDevice, setHasActiveDevice] = useState(false)

    const [serialNumber, setSerialNumber] = useState(null)
    const {data: deviceOptions} = useQuery([QueryKey, serialNumber], () => [])

    const [applicationCode, setApplicationCode] = useState(null)
    const {data: applicationCodeOptions} = useQuery(
        [ApplicationCodeQueryKey, applicationCode],
        () => [],
    )

    const {mutate} = useMutation(
        serialNumber => getListDevices(id, serialNumber),
        {
            onSuccess: data => {
                queryClient.setQueryData([QueryKey, serialNumber], () => data)
            },
        },
    )

    const {mutate: mutateApplicationCode} = useMutation(
        () => getListApplicationCodes(id, applicationCode),
        {
            onSuccess: data => {
                queryClient.setQueryData(
                    [ApplicationCodeQueryKey, applicationCode],
                    () => data,
                )
            },
        },
    )

    const [visible, setVisible] = useState(false)
    const handleShowForm = useCallback(async () => {
        blockOverflownScroll()
        setVisible(true)

        const data = await getActiveDeviceByUser(id)
        if (!!data?.serialNumber || !!data?.applicationCode) {
            if (!!data?.serialNumber) {
                setSerialNumber({
                    label: data?.label,
                    value: data?.serialNumber,
                })
            } else {
                if (!!data?.applicationCode) {
                    setApplicationCode({
                        label: data?.label,
                        value: data?.applicationCode,
                    })
                }
            }

            setHasActiveDevice(true)
            setMyDevice(data)
        }
    }, [id])

    const handleHideForm = useCallback(() => {
        unblockOverflownScroll()
        setVisible(false)
        setSerialNumber(null)
        setApplicationCode(null)
        setHasActiveDevice(false)
    }, [])

    const handleSearch = event => {
        mutate(event.query)
    }

    const handleSearchApplicationCode = event => {
        mutateApplicationCode(event.query)
    }

    const handleOnSubmit = async () => {
        if (!serialNumber && !applicationCode) {
            showNotification(
                severityNotification.warn,
                `Please select a serial number or application code !`,
            )
            return
        }

        let responseStatus = null

        if (!!serialNumber) {
            responseStatus = await activateDevice(id, serialNumber?.value, null)
        }

        if (!!applicationCode) {
            responseStatus = await activateDevice(
                id,
                null,
                applicationCode?.value,
            )
        }

        if (responseStatus && !!responseStatus?.id) {
            showNotification(
                severityNotification.success,
                'Activation signal has been sent successfully !',
            )

            // queryClient.setQueryData(
            //     ['User-Detail', user?.id, treatAsRole.Patient, id],
            //     oldData => {
            //         return {...oldData, deviceAssigned: true}
            //     },
            // )

            const {patientKeys} = QueryKeyManager(
                QueryKeyTypes.patient,
                user?.id,
            )

            queryClient.invalidateQueries(patientKeys.detail({id}))

            handleHideForm()
        }
    }

    const handleDeactivate = async () => {
        const responseStatus = await deactivateDevice(
            id,
            myDevice?.serialNumber,
            myDevice?.applicationCode,
        )
        if (responseStatus && !!responseStatus?.id) {
            showNotification(
                severityNotification.success,
                'Deactivation signal has been sent successfully !',
            )

            queryClient.setQueryData(
                ['User-Detail', user?.id, treatAsRole.Patient, id],
                oldData => {
                    return {...oldData, deviceAssigned: false}
                },
            )

            handleHideForm()
        }
    }

    return (
        <Dialog
            header={
                <h5 className=" mb-0 font-bold">
                    <strong>{translateKeys('Watch Remote Activation')}</strong>
                </h5>
            }
            style={{width: '40vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}
            visible={visible}
            onHide={() => handleHideForm()}>
            <div className="col-12 mb-3 px-0">
                <div className="col-12 mb-3">
                    <h5 className="font-bold">
                        {translateKeys('Serial Number')}
                    </h5>
                    <AutoComplete
                        className="col-12 h-3rem p-0"
                        inputClassName="col-12"
                        value={serialNumber}
                        disabled={!!hasActiveDevice}
                        onChange={e => {
                            setSerialNumber(e.value)
                            setApplicationCode(null)
                        }}
                        suggestions={deviceOptions || []}
                        completeMethod={handleSearch}
                        field="label"
                        placeholder={translateKeys(
                            'Find devices with serial number',
                        )}
                    />
                </div>
                <Divider align="center" layout="horizontal">
                    <span className="p-tag px-3 ">
                        {translateCommons('OR')}
                    </span>
                </Divider>
                <div className="col-12 mb-3">
                    <h5 className="font-bold">
                        {translateKeys('Application Code')}
                    </h5>
                    <AutoComplete
                        className="col-12 h-3rem p-0"
                        disabled={!!hasActiveDevice}
                        inputClassName="col-12"
                        value={applicationCode}
                        onChange={e => {
                            setApplicationCode(e.value)
                            setSerialNumber(null)
                        }}
                        suggestions={applicationCodeOptions || []}
                        completeMethod={handleSearchApplicationCode}
                        field="label"
                        placeholder={translateKeys(
                            'Find devices with application code',
                        )}
                    />
                </div>
                <div className="mb-3">
                    <p>
                        * {translateKeys('Note: it may take up to')}
                        <span className="font-bold">
                            {translateKeys('5 minutes')}
                        </span>{' '}
                        {translateKeys(
                            'to activate the watch application when the watch face is in sleep mode. If you select the device which is already assigned, it will',
                        )}
                        <span className="font-bold">
                            {translateKeys('override with the new patient')}
                        </span>{' '}
                        {translateKeys('and')}
                        <span className="font-bold">
                            {translateKeys("stop receiving old patient's data")}
                        </span>
                    </p>
                </div>
                <Divider align="center" layout="horizontal">
                    <span className="p-tag px-3 ">
                        {translateCommons('OR')}
                    </span>
                </Divider>
                <div className="col-12 mb-3">
                    <h5 className="font-bold mb-1">
                        {translateKeys('Access Code')}
                    </h5>
                    <div className="mb-3">
                        <label>
                            {translateCommons(
                                'You can also activate the watch application by using the access code. Please enter the access code in the watch application.',
                            )}
                        </label>
                    </div>
                    <InputText
                        readOnly
                        className="font-bold col-12 h-3rem"
                        value={props?.accessCode}
                        onChange={() => {}}
                    />
                </div>
            </div>
            <div className="my-3">
                <div>
                    <Button
                        className="col-12 p-button-lg"
                        label={translateKeys('SUBMIT')}
                        icon="pi pi-send"
                        onClick={handleOnSubmit}
                    />
                </div>
                {!!hasActiveDevice && (
                    <div className="mt-3">
                        <Button
                            className="col-12 p-button-lg p-button-text"
                            label={translateKeys('DEACTIVATE')}
                            icon="pi pi-trash"
                            onClick={handleDeactivate}
                        />
                    </div>
                )}
            </div>
        </Dialog>
    )
})

export default WatchRemoteActivation
